import { ColDef, GridApi, type ISetFilterParams } from 'ag-grid-community'
import { Download, Loader2, Plus, Search } from 'lucide-react'
import pluralize from 'pluralize'
import { useCallback, useEffect, useState } from 'react'

import { useTrackCallback } from '@/lib/analytics/events'
import { cn } from '@/lib/ui/style-helpers'

import { Button } from '@/components/lemmala-design-system/button/button'
import { DropdownSelect } from '@/components/lemmala-design-system/dropdown/dropdown-select'
import type { DropdownSelectItem } from '@/components/lemmala-design-system/dropdown/dropdown-select.types'
import { Input } from '@/components/lemmala-design-system/input/input'
import { useAgGridContext } from '@/components/lemmala-design-system/table'
import { DEFAULT_FILTER_TRACKING_EVENT } from '@/components/lemmala-design-system/table/consts'
import {
  CsvExportConfig,
  useAgCsvExport,
} from '@/components/lemmala-design-system/table/hooks/use-ag-csv-export'
import { useAgFilterManager } from '@/components/lemmala-design-system/table/hooks/use-ag-filter-manager'
import { TABLE_FILTER_BAR_LABELS } from '@/components/lemmala-design-system/table/labels'
import type {
  AgDataTableTrackingContext,
  CsvExportManager,
  DateFilterSelection,
  FilterManager,
  FilterModel,
  RowGroupingConfig,
} from '@/components/lemmala-design-system/table/types'
import { isDateFilterActive } from '@/components/lemmala-design-system/table/utils/date-range-filter'

import { AgColumnCustomization } from './ag-column-customization'
import { AgDateFilter } from './ag-date-range-filter'
import { AgFilter } from './ag-filter'

export interface AgFilterBarProps<TRowData extends object> {
  gridApi: GridApi<TRowData> | null
  columnDefs: ColDef<TRowData>[]
  quickFilterText?: string
  onSearch?: (searchText: string) => void
  searchContainerClassName?: string
  currentViewName?: string
  getViewDefinition?: (viewName: string) => {
    label?: string
    filterModel?: FilterModel
    columnState?: unknown[]
    filterBarFilters?: string[]
  }
  displayedCount?: number
  hideSearch?: boolean
  hideExport?: boolean
  hideCount?: boolean
  hideFilters?: boolean
  // Important to allow filter popovers to catch interactions
  inModal?: boolean
  columnCustomizationEnabled?: boolean
  agCsvExportConfig?: CsvExportConfig
  customLeftContent?: React.ReactNode
  /**
   * Persistence for user-added filter chips (provided by AgDataTable when enabled).
   * Must be referentially stable (memoized) — a fresh object per render re-runs
   * the stored-chips adoption effect.
   */
  chipStorage?: {
    read: () => string[] | undefined
    write: (chips: string[]) => void
  }
  trackingContext?: AgDataTableTrackingContext
  sticky?: boolean
  grouping?: RowGroupingConfig<TRowData>
  searchPlacement?: 'left' | 'right'
}

export const AgFilterBar = <TRowData extends object>({
  gridApi,
  columnDefs,
  quickFilterText = '',
  onSearch,
  searchContainerClassName,
  currentViewName,
  getViewDefinition,
  displayedCount = 0,
  hideSearch = false,
  hideExport = false,
  hideCount = false,
  hideFilters = false,
  columnCustomizationEnabled = false,
  agCsvExportConfig,
  customLeftContent,
  chipStorage,
  trackingContext,
  sticky = false,
  inModal,
  grouping,
  searchPlacement = 'right',
}: AgFilterBarProps<TRowData>) => {
  const ctx = useAgGridContext<TRowData>()
  const [internalCount, setInternalCount] = useState<number>(displayedCount)
  const [isAddFilterOpen, setIsAddFilterOpen] = useState(false)

  // Analytics tracking
  const trackFilterApply = useTrackCallback(
    trackingContext?.eventName ?? DEFAULT_FILTER_TRACKING_EVENT,
  )

  const [additionalFilters, setAdditionalFiltersState] = useState<string[]>(
    () => chipStorage?.read() ?? [],
  )

  // chipStorage can appear after mount (user/tenant scope loads async); adopt the
  // stored chips then, unless the user has already added chips this session.
  useEffect(() => {
    const stored = chipStorage?.read()
    if (stored?.length) {
      // oxlint-disable-next-line react/set-state-in-effect
      setAdditionalFiltersState((prev) => (prev.length ? prev : stored))
    }
  }, [chipStorage])

  const [newlyAddedFilterId, setNewlyAddedFilterId] = useState<string | null>(null)

  // Sync additionalFilters with localStorage
  const setAdditionalFilters = useCallback(
    (filtersOrUpdater: string[] | ((prev: string[]) => string[])) => {
      setAdditionalFiltersState((prev) => {
        const newFilters =
          typeof filtersOrUpdater === 'function' ? filtersOrUpdater(prev) : filtersOrUpdater
        const newlyAddedFilterId = newFilters[newFilters.length - 1] ?? null
        setNewlyAddedFilterId(newlyAddedFilterId)
        chipStorage?.write(newFilters)
        return newFilters
      })
    },
    [chipStorage],
  )

  const filterManager: FilterManager = useAgFilterManager(gridApi, {
    getViewDefinition: (viewName) => {
      const def =
        (getViewDefinition && viewName && getViewDefinition(viewName)) ||
        (ctx?.getViewDefinition && viewName && ctx.getViewDefinition(viewName)) ||
        undefined
      return { filterModel: (def?.filterModel as FilterModel) || {} }
    },
  })

  const { exportToCsv, isExporting }: CsvExportManager = useAgCsvExport(gridApi, agCsvExportConfig)

  // Get all filterable columns (columns with agSetColumnFilter)
  const allFilterableColumns = columnDefs.filter(
    (col) =>
      col.headerName &&
      (col.field || col.colId) &&
      ((col.filter === 'agSetColumnFilter' &&
        ((col.filterParams as ISetFilterParams<TRowData, string>)?.values ||
          (col.filterParams as { personFilter?: boolean })?.personFilter)) ||
        col.filter === 'agDateColumnFilter') &&
      (!ctx?.removableFilters || !ctx.removableFilters.includes(String(col.colId || col.field))),
  )

  // Get default filter IDs from view definition or context default
  const effectiveViewName = currentViewName || ctx?.currentViewName
  const viewDef = effectiveViewName
    ? (getViewDefinition && getViewDefinition(effectiveViewName)) ||
      (ctx?.getViewDefinition && ctx.getViewDefinition(effectiveViewName)) ||
      null
    : null
  const defaultFilterIds =
    (viewDef?.filterBarFilters as string[]) || ctx?.defaultVisibleFilters || []

  // Any filter that's currently active (e.g. from URL) but not surfaced by the
  // view or user config — show it as a chip so the user isn't filtered by
  // something invisible. It disappears again once cleared.
  const activeFilterIds = Object.entries(filterManager.filterModel)
    .filter(([key, filter]) => {
      // *Base keys are internal filter-helper columns (e.g. `lifecycleBase`) that
      // back the filtering of a visible column but aren't user-facing themselves.
      // Same reason they're stripped from URL serialization in use-ag-url-sync.ts.
      if (key.endsWith('Base')) return false
      if (filter?.filterType === 'set')
        return Array.isArray(filter.values) && filter.values.length > 0
      if (filter?.filterType === 'date') return isDateFilterActive(filter)
      return false
    })
    .map(([key]) => key)

  // Combine default filters with additional filters (deduplicated to prevent duplicates)
  const visibleFilterIds = [
    ...new Set([...defaultFilterIds, ...additionalFilters, ...activeFilterIds]),
  ]

  // Get column definitions for visible filters, maintaining order
  const filterableColumns: ColDef<TRowData>[] = visibleFilterIds
    .map((field) => allFilterableColumns.find((col) => (col.colId || col.field) === field))
    .filter((col): col is ColDef<TRowData> => !!col)

  const getColumnDefAgKey = useCallback(
    (uiField: string): string => {
      // Prefer using colId for addressing filters to avoid ambiguity with duplicate fields
      const colDef = columnDefs.find((c) => (c.colId || c.field) === uiField)
      const key = colDef?.colId || colDef?.field || uiField
      return key
    },
    [columnDefs],
  )

  const onFilterChanged = (uiField: string, values: string[]) => {
    const agKey = getColumnDefAgKey(uiField)
    filterManager.setFilter(agKey, values)

    // Track filter application
    if (trackFilterApply) {
      trackFilterApply({
        field: uiField,
        valueCount: values.length,
        values: values.slice(0, 10), // Limit to first 10 values to avoid excessive data
        action: 'apply',
      })
    }
  }

  const onDateFilterChanged = (uiField: string, selection: DateFilterSelection) => {
    const agKey = getColumnDefAgKey(uiField)
    filterManager.setDateFilter(agKey, selection)

    if (trackFilterApply) {
      trackFilterApply({
        field: uiField,
        action: 'apply',
        ...selection,
      })
    }
  }

  const onFilterClear = (uiField: string) => {
    const agKey = getColumnDefAgKey(uiField)
    filterManager.clearFilter(agKey)

    // Track filter clear
    if (trackFilterApply) {
      trackFilterApply({
        field: uiField,
        action: 'clear',
      })
    }
  }

  // Add a new filter to the filter bar
  const handleAddFilter = useCallback(
    (filterId: string) => {
      setAdditionalFilters((prev) => [...prev, filterId])
      setIsAddFilterOpen(false)
    },
    [setAdditionalFilters],
  )

  // Remove a dynamically added filter
  const handleRemoveFilter = useCallback(
    (filterId: string) => {
      setAdditionalFilters((prev) => prev.filter((id) => id !== filterId))
      // Also clear any active filter values for this filter
      const agKey = getColumnDefAgKey(filterId)
      filterManager.clearFilter(agKey)
    },
    [filterManager, getColumnDefAgKey, setAdditionalFilters],
  )

  // Get available filters (not yet shown in the filter bar)
  const availableFilters: DropdownSelectItem[] = allFilterableColumns
    .filter((col) => {
      const colKey = (col.colId || col.field) as string
      return !visibleFilterIds.includes(colKey)
    })
    .map((col) => ({
      id: (col.colId || col.field) as string,
      label: col.headerName || '',
    }))

  // Count filters the user added on top of the current view (view tabs supply their own).
  const viewFilterModel = (viewDef?.filterModel as FilterModel) || {}
  const activeFilterCount = Object.entries(filterManager.filterModel).filter(([key, filter]) => {
    if (key in viewFilterModel) return false
    if (filter.filterType === 'set') {
      return Array.isArray(filter.values) && filter.values.length > 0
    }

    return filter.filterType === 'date' && isDateFilterActive(filter)
  }).length

  // Clear all filters
  const handleClearAllFilters = useCallback(() => {
    filterManager.clearAllFilters()
    setAdditionalFilters([])

    // Track clear all action
    if (trackFilterApply) {
      trackFilterApply({
        action: 'clear_all',
      })
    }
  }, [filterManager, setAdditionalFilters, trackFilterApply])

  // Keep results count synced with grid state
  // Exclude detail rows (master-detail expanded rows) from the count
  useEffect(() => {
    if (!gridApi)
      return () => {
        /* no-op */
      }
    const updateCount = () => {
      // Custom grouping logic: handles collapsed/expanded group headers
      if (grouping) {
        let count = 0
        const currentFilterModel = gridApi.getFilterModel()
        const hasActiveFilter =
          currentFilterModel &&
          Object.keys(currentFilterModel).length > 0 &&
          !!grouping.doesChildMatchFilter

        gridApi.forEachNodeAfterFilter((node) => {
          if (!node.data) return

          if (grouping.isGroupHeader(node.data)) {
            // For group headers:
            // - If collapsed: count children that match the current search query and filter
            // - If expanded: don't count (children are visible and will be counted individually)
            if (!grouping.isExpanded(node.data)) {
              const children = grouping.getChildren(node.data)
              const query = quickFilterText?.trim() || ''
              const hasSearch = query.length >= 2 && !!grouping.doesChildMatchSearch

              if (!hasSearch && !hasActiveFilter) {
                count += children.length
              } else {
                count += children.filter((c) => {
                  const matchesSearch = !hasSearch || grouping.doesChildMatchSearch!(c, query)
                  const matchesFilter =
                    !hasActiveFilter || grouping.doesChildMatchFilter!(c, currentFilterModel)
                  return matchesSearch && matchesFilter
                }).length
              }
            }
          } else {
            // Regular data row (visible when group is expanded)
            count++
          }
        })
        setInternalCount(count)
        return
      }

      let count = 0
      gridApi.forEachNodeAfterFilter((node) => {
        // Only count leaf nodes (actual data rows, not group headers)
        if (!node.group && node.data) {
          count++
        }
      })
      setInternalCount(count)
    }
    updateCount()
    gridApi.addEventListener('filterChanged', updateCount)
    gridApi.addEventListener('modelUpdated', updateCount)
    gridApi.addEventListener('rowDataUpdated', updateCount as (e: unknown) => void)
    return () => {
      gridApi.removeEventListener('filterChanged', updateCount)
      gridApi.removeEventListener('modelUpdated', updateCount)
      gridApi.removeEventListener('rowDataUpdated', updateCount as (e: unknown) => void)
    }
  }, [gridApi, grouping, quickFilterText])

  if (!gridApi) return null

  const searchInput = (
    <div className={cn('w-72', searchContainerClassName)}>
      <Input
        placeholder={TABLE_FILTER_BAR_LABELS.searchPlaceholder}
        value={quickFilterText}
        onChange={(e) => onSearch?.(e.target.value)}
        iconLeft={<Search className='size-4 text-main-foreground-tertiary' />}
      />
    </div>
  )

  return (
    <div
      className={cn(
        'flex w-full flex-col gap-2.5',
        sticky && 'sticky top-0 z-20 bg-main-background-canvas py-4',
      )}
    >
      <div
        className={cn(
          'flex w-full items-start',
          hideFilters && !customLeftContent ? 'justify-end' : 'justify-between',
        )}
      >
        {hideFilters && customLeftContent && (
          <div className='flex flex-wrap items-center gap-2.5'>{customLeftContent}</div>
        )}
        {!hideFilters && (
          <div className='flex flex-wrap items-center gap-2.5'>
            {/* Regular filters */}
            {filterableColumns.map((colDef) => {
              const fieldKey = (colDef.field as string) || (colDef.colId as string)
              const colIdKey = (colDef.colId as string) || (colDef.field as string)
              const selectedFilter =
                filterManager.filterModel[fieldKey] || filterManager.filterModel[colIdKey]
              const isAdditional = additionalFilters.includes(colIdKey)
              const isNewlyAdded = newlyAddedFilterId === colIdKey

              if (colDef.filter === 'agDateColumnFilter') {
                const selectedDateFilter =
                  selectedFilter?.filterType === 'date' ? selectedFilter : undefined

                return (
                  <AgDateFilter
                    key={colDef.colId ?? String(colDef.field ?? colDef.headerName)}
                    columnDef={colDef}
                    selected={selectedDateFilter}
                    defaultOpen={isNewlyAdded}
                    onChange={onDateFilterChanged}
                    onClear={onFilterClear}
                    onRemove={isAdditional ? () => handleRemoveFilter(colIdKey) : undefined}
                    inModal={inModal}
                  />
                )
              }

              const selected = selectedFilter?.filterType === 'set' ? selectedFilter.values : []

              return (
                <AgFilter
                  key={colDef.colId ?? String(colDef.field ?? colDef.headerName)}
                  columnDef={colDef}
                  selectedValues={selected}
                  defaultOpen={isNewlyAdded}
                  onChange={onFilterChanged}
                  onClear={onFilterClear}
                  onRemove={isAdditional ? () => handleRemoveFilter(colIdKey) : undefined}
                  hideSuffix={isAdditional}
                  inModal={inModal}
                />
              )
            })}

            {availableFilters.length > 0 && (
              <DropdownSelect
                open={isAddFilterOpen}
                onOpenChange={setIsAddFilterOpen}
                trigger={
                  <Button
                    type='button'
                    variant='ghost'
                    size='sm'
                    className='h-8 gap-1.5 px-2.5 text-main-foreground-secondary'
                  >
                    <Plus className='size-3.5' />
                    {TABLE_FILTER_BAR_LABELS.addFilter}
                  </Button>
                }
                items={availableFilters}
                selectedItems={[]}
                onItemSelect={(value) => {
                  if (value) handleAddFilter(value)
                }}
                search
                searchPlaceholder={TABLE_FILTER_BAR_LABELS.searchFilters}
                showTooltips
              />
            )}

            {/* Clear all when the user has filters beyond the current view */}
            {activeFilterCount >= 1 && (
              <Button
                type='button'
                variant='ghost'
                size='sm'
                onClick={handleClearAllFilters}
                className='h-8 px-2.5 text-main-foreground-accent text-label-small'
                aria-label={TABLE_FILTER_BAR_LABELS.clearAllFilters}
              >
                {TABLE_FILTER_BAR_LABELS.clearAll}
              </Button>
            )}

            {searchPlacement === 'left' && !hideSearch && searchInput}
          </div>
        )}

        {(!hideSearch ||
          !hideExport ||
          (!hideFilters && customLeftContent) ||
          columnCustomizationEnabled) && (
          <div className='flex shrink-0 items-center gap-3'>
            {!hideFilters && customLeftContent}

            {searchPlacement === 'right' && !hideSearch && searchInput}

            {columnCustomizationEnabled && (
              <AgColumnCustomization<TRowData>
                gridApi={gridApi}
                defaultColDefs={ctx?.originalColumnDefs ?? columnDefs}
              />
            )}

            {!hideExport && (
              <Button
                type='button'
                variant='outline'
                size='icon'
                aria-label={TABLE_FILTER_BAR_LABELS.downloadCsv}
                onClick={exportToCsv}
                disabled={!gridApi || isExporting}
              >
                {isExporting ? (
                  <Loader2 className='size-4 animate-spin text-main-foreground-secondary' />
                ) : (
                  <Download className='size-4 text-main-foreground-secondary' />
                )}
              </Button>
            )}
          </div>
        )}
      </div>

      {!hideCount && (
        <div className='flex w-full items-start justify-start'>
          <div className='flex items-center gap-2.5 pl-2'>
            <span
              className='text-main-foreground-secondary text-body-small'
              data-testid='result-count'
            >
              {pluralize('Result', internalCount, true)}
            </span>
          </div>
        </div>
      )}
    </div>
  )
}
